/**
 * App-level data singletons + React bindings.
 *
 * One PERSONAL context (WorkspaceStore + NoteDocManager, swapped on
 * sign-in/out) plus zero or more SHARED contexts, one per mounted shared
 * folder. All cloud contexts share ONE SyncManager (one websocket; Hocuspocus
 * multiplexes by doc name). Shared contexts are online-only: no IndexedDB,
 * loaded means first server sync.
 *
 * Item ids are 16-char nanoids, globally unique in practice, so bare-id
 * consumers (tabs, recents, the editor registry) stay unchanged and resolve
 * through resolveItem().
 */
import { useSyncExternalStore } from "react";
import { docName, type WorkspaceItem } from "@hydra-notes/shared";
import { WorkspaceStore, LOCAL_WORKSPACE_ID } from "./workspace";
import { NoteDocManager } from "./notes";
import { TaskDocManager } from "./taskDocs";
import { SyncManager, userIdentity } from "./sync";
import { searchService } from "./search/client";
import { useSharedFolders, type SharedFolderMeta } from "./sharedFolders";
import { useAuth } from "./auth";
import { toast } from "@/ui/toast";
import { flushPendingDeletes, purgeExpiredTrash, watchTrashRetention } from "./permanentDelete";

export interface DataContext {
  /** This context's space id: the personal workspace id or a folder id. */
  workspaceId: string;
  kind: "personal" | "shared";
  workspace: WorkspaceStore;
  notes: NoteDocManager;
  tasks: TaskDocManager;
  sync: SyncManager | null;
  destroyed: boolean;
}

let personal: DataContext | null = null;
const shared = new Map<string, DataContext>();
const retentionWatchers = new Map<DataContext, () => void>();
/** One socket for every cloud space; created with the cloud personal ctx. */
let sharedSync: SyncManager | null = null;
const ctxListeners = new Set<() => void>();

function notifyCtx(): void {
  for (const l of ctxListeners) l();
}

function startRetentionWatcher(ctx: DataContext): void {
  retentionWatchers.get(ctx)?.();
  retentionWatchers.set(ctx, watchTrashRetention(ctx));
}

function stopRetentionWatcher(ctx: DataContext): void {
  retentionWatchers.get(ctx)?.();
  retentionWatchers.delete(ctx);
}

/**
 * The personal context by default; pass a space id for a mounted shared
 * folder. Throws on an unmounted shared id: callers that may race an unmount
 * should use resolveItem/ctxFor and handle undefined.
 */
export function getData(spaceId?: string): DataContext {
  if (!personal) {
    personal = createPersonalContext(LOCAL_WORKSPACE_ID);
  }
  if (!spaceId || spaceId === personal.workspaceId) return personal;
  const ctx = shared.get(spaceId);
  if (!ctx) throw new Error(`space ${spaceId} is not mounted`);
  return ctx;
}

export function ctxFor(spaceId: string): DataContext | undefined {
  if (personal?.workspaceId === spaceId) return personal;
  return shared.get(spaceId);
}

/** Every mounted context, personal first. */
export function allContexts(): DataContext[] {
  return personal ? [personal, ...shared.values()] : [...shared.values()];
}

/** Find which mounted space an item lives in. */
export function resolveItem(itemId: string): { ctx: DataContext; item: WorkspaceItem } | undefined {
  for (const ctx of allContexts()) {
    if (ctx.destroyed) continue;
    const item = ctx.workspace.getItem(itemId);
    if (item) return { ctx, item };
  }
  return undefined;
}

function createPersonalContext(workspaceId: string): DataContext {
  const isCloud = workspaceId !== LOCAL_WORKSPACE_ID;
  const sync = isCloud ? (sharedSync ??= new SyncManager()) : null;
  // Content cleanup needs the managers below, so the context orchestrates
  // retention instead of letting WorkspaceStore remove tree rows by itself.
  const workspace = new WorkspaceStore(workspaceId, { purgeTrashOnLoad: false });
  if (sync) sync.attach(docName.workspace(workspaceId), workspace.doc);
  const notes = new NoteDocManager(workspaceId, sync);
  const tasks = new TaskDocManager(workspaceId, sync);
  // client search (Dexie + MiniSearch) indexes the personal space ONLY:
  // shared folders are online-only, so nothing of theirs may persist locally
  searchService.attach(workspace, notes);
  const ctx: DataContext = { workspaceId, kind: "personal", workspace, notes, tasks, sync, destroyed: false };
  void workspace.whenLoaded.then(async () => {
    if (isCloud) await flushPendingDeletes(workspaceId);
    await purgeExpiredTrash(ctx);
    if (!ctx.destroyed) startRetentionWatcher(ctx);
  });
  return ctx;
}

/** Swap the personal workspace (sign-in/out). Unmounts every shared space. */
export function switchWorkspace(workspaceId: string): DataContext {
  if (personal?.workspaceId === workspaceId) return personal;
  for (const id of [...shared.keys()]) unmountSharedSpace(id, { silent: true });
  if (personal) {
    searchService.detach();
    personal.destroyed = true;
    stopRetentionWatcher(personal);
    personal.notes.destroyAll();
    personal.tasks.destroyAll();
    personal.workspace.destroy();
  }
  // every cloud context is gone; drop the socket rather than leak providers
  if (sharedSync) {
    sharedSync.destroy();
    sharedSync = null;
  }
  personal = createPersonalContext(workspaceId);
  notifyCtx();
  return personal;
}

// ---------- shared space mounting ----------

function mountSharedSpace(meta: SharedFolderMeta): void {
  if (shared.has(meta.id)) return;
  if (!personal || personal.workspaceId === LOCAL_WORKSPACE_ID) return;
  const sync = (sharedSync ??= new SyncManager());

  const onAuthFailed = () => {
    // locked out or removed mid-session; mark now, confirm via refresh
    const st = useSharedFolders.getState();
    st.markLocked(meta.id);
    void st.refresh();
  };

  const user = useAuth.getState().user;
  const identity = user ? userIdentity(user.id, user.displayName || user.email) : undefined;

  const workspace = new WorkspaceStore(meta.id, {
    persist: false,
    channelName: `hn.sf.${meta.id}`,
    purgeTrashOnLoad: false,
  });
  const attachment = sync.attach(docName.sharedFolder(meta.id), workspace.doc, {
    spaceId: meta.id,
    ...(identity ? { identity } : {}),
    onAuthFailed,
  });
  const notes = new NoteDocManager(meta.id, sync, {
    persist: false,
    attach: { ...(identity ? { identity } : {}), onAuthFailed },
  });
  const tasks = new TaskDocManager(meta.id, sync, {
    persist: false,
    attach: { ...(identity ? { identity } : {}), onAuthFailed },
  });
  const ctx: DataContext = { workspaceId: meta.id, kind: "shared", workspace, notes, tasks, sync, destroyed: false };
  shared.set(meta.id, ctx);
  void attachment.whenSynced.then(async () => {
    workspace.markLoaded();
    await flushPendingDeletes(meta.id);
    // trash is owner-only, so only the owner's mount purges expired trash
    if (meta.role === "owner") {
      await purgeExpiredTrash(ctx);
      if (!ctx.destroyed) startRetentionWatcher(ctx);
    }
  });
  notifyCtx();
}

export function unmountSharedSpace(folderId: string, opts?: { silent?: boolean; reason?: string }): void {
  const ctx = shared.get(folderId);
  if (!ctx) return;
  // capture the space's item ids BEFORE destroy, then close their tabs
  const itemIds = ctx.workspace.getSnapshot().map((i) => i.id);
  ctx.destroyed = true;
  stopRetentionWatcher(ctx);
  shared.delete(folderId);
  ctx.notes.destroyAll();
  ctx.tasks.destroyAll();
  ctx.workspace.destroy();
  notifyCtx();
  if (itemIds.length) {
    // late imports avoid a module cycle (these stores never import data/)
    void Promise.all([import("@/store/tabs"), import("@/store/recents")]).then(([tabs, recents]) => {
      tabs.useTabs.getState().closeItemEverywhere(itemIds);
      recents.useRecents.getState().remove(itemIds);
    });
  }
  if (!opts?.silent && opts?.reason) toast.info(opts.reason);
}

/**
 * Reconcile mounted shared contexts against the folder list: mount unlocked
 * memberships, unmount locked or removed ones. Called from boot whenever the
 * folder store changes.
 */
export function syncSharedMounts(folders: SharedFolderMeta[]): void {
  if (!personal || personal.workspaceId === LOCAL_WORKSPACE_ID) return;
  const wanted = new Map(folders.filter((f) => !f.locked).map((f) => [f.id, f]));
  for (const id of [...shared.keys()]) {
    if (!wanted.has(id)) {
      const meta = folders.find((f) => f.id === id);
      unmountSharedSpace(id, {
        reason: meta?.locked ? `"${meta.title}" is locked: it no longer fits in your storage` : undefined,
        silent: !meta?.locked,
      });
    }
  }
  for (const meta of wanted.values()) mountSharedSpace(meta);
}

export function subscribeDataContext(cb: () => void): () => void {
  ctxListeners.add(cb);
  return () => ctxListeners.delete(cb);
}

// ---------- React hooks (survive context swaps) ----------

/** Subscribe to every mounted context's workspace plus mount changes. */
function subscribeAllSpaces(cb: () => void): () => void {
  let unsubs = allContexts().map((c) => c.workspace.subscribe(cb));
  const unsubCtx = subscribeDataContext(() => {
    for (const u of unsubs) u();
    unsubs = allContexts().map((c) => c.workspace.subscribe(cb));
    combinedDirty = true;
    cb();
  });
  return () => {
    for (const u of unsubs) u();
    unsubCtx();
  };
}

export interface SpaceItems {
  spaceId: string;
  kind: "personal" | "shared";
  items: WorkspaceItem[];
}

// cached combined snapshot: useSyncExternalStore needs a stable reference
let combined: SpaceItems[] = [];
let combinedDirty = true;
let combinedParts: WorkspaceItem[][] = [];

function getCombinedSnapshot(): SpaceItems[] {
  const ctxs = allContexts();
  const parts = ctxs.map((c) => c.workspace.getSnapshot());
  if (!combinedDirty && parts.length === combinedParts.length && parts.every((p, i) => p === combinedParts[i])) {
    return combined;
  }
  combinedParts = parts;
  combined = ctxs.map((c, i) => ({ spaceId: c.workspaceId, kind: c.kind, items: parts[i]! }));
  combinedDirty = false;
  return combined;
}

/** Items of every mounted space (personal first). */
export function useAllSpaceItems(): SpaceItems[] {
  return useSyncExternalStore(subscribeAllSpaces, getCombinedSnapshot);
}

/** Personal workspace items only (bulk ops, migration, templates). */
export function useWorkspaceItems(): WorkspaceItem[] {
  return useSyncExternalStore(
    (cb) => {
      let unsubWs = getData().workspace.subscribe(cb);
      const unsubCtx = subscribeDataContext(() => {
        unsubWs();
        unsubWs = getData().workspace.subscribe(cb);
        cb();
      });
      return () => {
        unsubWs();
        unsubCtx();
      };
    },
    () => getData().workspace.getSnapshot(),
  );
}

/** Look an item up across every mounted space. */
export function useItem(id: string | null): WorkspaceItem | undefined {
  const spaces = useAllSpaceItems();
  if (!id) return undefined;
  for (const s of spaces) {
    const item = s.items.find((i) => i.id === id);
    if (item) return item;
  }
  return undefined;
}

/** Which space an item lives in (undefined while unresolved/unmounted). */
export function useSpaceOf(id: string | null): { spaceId: string; kind: "personal" | "shared" } | undefined {
  const spaces = useAllSpaceItems();
  if (!id) return undefined;
  for (const s of spaces) {
    if (s.items.some((i) => i.id === id)) return { spaceId: s.spaceId, kind: s.kind };
  }
  return undefined;
}

export function useDataContextId(): string {
  return useSyncExternalStore(subscribeDataContext, () => getData().workspaceId);
}
